import type { DamageType } from "./combat.ts";
import type { BiomeName } from "./resources.ts";

/**
 * Criaturas (doc 06, Arquitetura e Bestiário): dados por tipo aqui, comportamento por arquétipo
 * no servidor. Valores do dia 0, antes das curvas de dificuldade (F14). Hipóteses de playtest.
 */
export type CreatureArchetype = "neutra" | "assombracao" | "chefe" | "guardiao" | "final";

/**
 * Estados da máquina (doc 06, Máquina de estados): o índice vai no schema (1 byte). Estados novos
 * entram antes de `morta`, que fica por último.
 */
export const CREATURE_STATES = [
  "surgindo",
  "vagando",
  "alerta",
  "fugindo",
  "preparando",
  "golpeando",
  "recuperando",
  "cambaleando",
  "enrolado",
  "cavando",
  "perseguindo",
  "vulneravel",
  "dissolvendo",
  "sumindo",
  "morta",
] as const;
export type CreatureState = (typeof CREATURE_STATES)[number];
export const stateIndex = (state: CreatureState): number => CREATURE_STATES.indexOf(state);

/**
 * Como o ataque acontece (doc 06, Bestiário): `golpe` parado (arco ou círculo em volta), `salto` e
 * `carga` atravessando a faixa anunciada, `mergulho` do alto até o círculo, `arremesso` caindo no
 * círculo depois do voo, `agarrar` golpe curto que arremessa o jogador, `marcas` círculos no chão
 * sob os jogadores (chuva de caldeirões, raízes).
 */
export type AttackKind = "golpe" | "salto" | "carga" | "mergulho" | "arremesso" | "agarrar" | "marcas";
/** Forma do aviso no chão e da área de acerto: arco à frente, círculo ou faixa (linha). */
export type AttackShape = "arco" | "circulo" | "linha";

/** Área no chão que fica machucando depois do ataque (rastro de fogo, poça, mato fechado, brasas). */
export interface AttackHazard {
  kind: "fogo" | "poca" | "mato" | "brasa";
  /** Raio da poça ou meia-largura do rastro, em metros. */
  radius: number;
  seconds: number;
  /** Dano a cada `tick` segundos para quem está dentro. */
  tick: number;
  damage: number;
  /** Lentidão enquanto está dentro (0,4 = −40 % de velocidade). */
  slow?: number;
}

export interface CreatureAttack {
  id: string;
  kind: AttackKind;
  /** Preparo com aviso no chão e recuperação, em segundos (doc 06, Ataques; aviso mínimo 0,5 s, doc 07 A8). */
  windup: number;
  recovery: number;
  shape: AttackShape;
  /** Alcance do arco, raio do círculo ou comprimento máximo da faixa. */
  range: number;
  /** Abertura total do arco, em graus. */
  angle: number;
  /** Largura da faixa (`linha`). */
  width?: number;
  damage: number;
  /** Fração do dano na borda do círculo (cai linearmente do centro até ela). */
  edge?: number;
  /** Círculo vazado (anel): sem dano a menos disto do centro (chicote do Boitatá). */
  inner?: number;
  types: readonly DamageType[];
  /** Distância do alvo em que a criatura escolhe este ataque (mín., máx.). */
  use: readonly [number, number];
  /** Velocidade do deslocamento em `salto`, `carga` e `mergulho` (m/s). */
  speed?: number;
  /** Segundos vulnerável depois (fim do salto, pouso do mergulho, carga que bate num obstáculo). */
  vulnerable?: number;
  /** Voo do arremesso, em segundos (mín., máx., pela distância). */
  flight?: readonly [number, number];
  /** Recarga própria do ataque, sorteada no intervalo, em segundos. */
  every?: readonly [number, number];
  /** Empurrão no jogador atingido, em m/s (agarrar e arremessar). */
  push?: number;
  hazard?: AttackHazard;
  /** Golpe que bate de novo na mesma área: `count` acertos, a cada `every` s, a partir do fim do preparo (bafo, fogo, pancada dupla). */
  pulses?: { count: number; every: number };
  /** `marcas`: quantas, o intervalo entre elas (0 = todas juntas) e, se `perPlayer`, uma sob cada jogador (mín., máx.). */
  marks?: { count: number; gap: number; perPlayer?: readonly [number, number] };
  /** Em quem apanha: prende (s), deixa lento (fração da velocidade que sobra, s) e trava a recarga do Patuá (s). */
  root?: number;
  slow?: { factor: number; seconds: number };
  patuaBlock?: number;
  /** Só contra quem está atrás, a até `range` (coice). */
  rear?: boolean;
  /** Fase mínima do chefe em que o ataque vale (`minPhase`) ou a fase exata (`phase`). */
  phase?: number;
  minPhase?: number;
  /** Emenda este ataque logo depois (Mapinguari na fase 2). */
  then?: string;
  /** Repete o ataque `chain` vezes seguidas, cada uma com preparo (investidas da Cabra). */
  chain?: number;
  /** Recarga compartilhada entre variantes do mesmo padrão (a da fase 2 herda a da fase 1). */
  group?: string;
  /** Mira à frente: fração do movimento do alvo durante o preparo (área prevista 70 %, raios 50 %). */
  lead?: number;
  /** `marcas`: a criatura salta e pousa na marca quando ela acerta (salto do Guardião). */
  leap?: boolean;
  /** Só acerta quem está no chão (pular evita: chicote do Boitatá) e joga para cima (m/s). */
  groundOnly?: boolean;
  toss?: number;
  /** Mira à frente limitada a este tempo (s): o cuspe do Boitatá prevê no máximo 1 s. */
  leadCap?: number;
  /**
   * Mecânicas de grupo dos chefes (F69, doc 09 §4): `share` ("juntar") divide o dano entre quem está na
   * área; `buster` ("golpe no tanque") é a marca pesada sob o alvo da vez; `interruptible`, o preparo longo
   * que a provocação, o Salto do Falcão ou o Rompe-Núcleo cortam (o chefe cambaleia).
   */
  share?: boolean;
  buster?: boolean;
  interruptible?: boolean;
  /** Usa o clipe de outro ataque da criatura (`golpe:<clip>`), sem clipe próprio no modelo (F69). */
  clip?: string;
}

/** Nome do clipe do golpe de um ataque: o próprio id ou o do ataque que ele reaproveita. */
export const attackClip = (attack: Pick<CreatureAttack, "id" | "clip">): string => attack.clip ?? attack.id;

export interface CreatureDrop {
  item: string;
  min: number;
  max: number;
  chance: number;
}

/**
 * Chefes (doc 06, Chefes): limiar da fase 2, tostões, invocações, sumiço, aura e o raio de
 * distância que a Cuca Rainha mantém. Regras comuns (teto por golpe, quebras de postura) em `BOSS`.
 */
export interface BossRules {
  /** Fração da vida abaixo da qual entra na fase 2. */
  phase2: number;
  /** Chance de uma Simpatia Lendária extra (Corpo-Seco Ancião). */
  legendaryChance?: number;
  /** Invoca `count + ⌊n/2⌋·perTwoPlayers` do tipo a cada `every` s, com no máximo `max` vivas (só na fase indicada). */
  summon?: { type: string; count: number; perTwoPlayers: number; every: number; max: number; phase?: 1 | 2 };
  /** Some em fumaça a cada `every` s por `seconds` e reaparece a uma distância (mín., máx.). */
  vanish?: { every: number; seconds: number; distance: readonly [number, number] };
  /** Quem está a até `radius` perde Fome `hungerMul` vezes mais rápido. */
  aura?: { radius: number; hungerMul: number };
  keepDistance?: readonly [number, number];
  /** Sem quebras de postura (Guardiões: só o teto de 5 % por golpe). */
  noBreaks?: boolean;
  /** Coleira em volta de onde despertou (m) e regeneração (fração da vida por segundo) sem ninguém nela. */
  leash?: number;
  regen?: number;
  /** Cópias de 1 de vida que imitam os ataques sem dano, ao cruzar cada fração da vida (Guardião Rosa). */
  decoys?: { at: readonly number[]; count: number };
  /** Limiares das fases (frações da vida, decrescentes); sem eles, só `phase2`. */
  phases?: readonly number[];
  /** Teto de dano por golpe (fração da vida máxima); sem ele, o comum dos chefes (5 %). */
  hitCap?: number;
}

/** Fauna (neutras): bando por zona, reposição e onde as zonas aparecem. */
export interface FaunaRules {
  /** Bando por zona (mín., máx.) e reposição em segundos. */
  flock: readonly [number, number];
  respawn: number;
  /** Pesos por bioma, distância máxima do mar, declive máximo, densidade por célula e raio da zona. */
  zone: { biomes: Partial<Record<BiomeName, number>>; maxCoastDistance?: number; maxSlope: number; density: number; radius: number };
}

/**
 * Assombrações (noite): custo no Orçamento de Assombro, rampa de peso por dia (doc 01, Ondas) e
 * como caçam (doc 06, Bestiário).
 */
export interface HauntRules {
  cost: number;
  startDay: number;
  peakDay: number;
  maxWeight: number;
  /** Tostões soltos ao morrer (a bolsa chega na F19). */
  coins: readonly [number, number];
  /** Quem prefere caçar: o de menor custo, o mais isolado, o de campo aberto ou quem mais bateu. */
  target: "custo" | "isolado" | "aberto" | "dano";
  /** Mantém esta distância do alvo (atiradora). */
  keepDistance?: readonly [number, number];
  /** Circula o alvo a esta distância antes de atacar. */
  orbit?: readonly [number, number];
  /** Voa a esta altura sobre o chão. */
  fly?: { altitude: number };
  /** Não cambaleia (postura nunca estoura). */
  staggerImmune?: boolean;
}

export interface CreatureType {
  id: string;
  index: number;
  name: string;
  /** Artigo dos chefes nos textos ("Derrubar o Ciclope"); as demais criaturas não usam. */
  article?: "o" | "a";
  archetype: CreatureArchetype;
  hp: number;
  /** Andar e fugir, em m/s. */
  speed: number;
  fleeSpeed: number;
  /** Percebe jogadores a esta distância (agachados, a `crouchPerception`). */
  perception: number;
  crouchPerception: number;
  /** Corpo: raio e altura (alvo do golpe, colisão com o mundo). */
  radius: number;
  height: number;
  defense: number;
  weaknesses: readonly DamageType[];
  /** Postura: dano acumulado em 2 s acima desta fração da vida faz cambalear 0,6 s. */
  poise: number;
  drops: readonly CreatureDrop[];
  /** Comportamentos próprios das neutras: fuga, alerta ao bando, enrolar, cavar, encurralada. */
  behavior: {
    fleeSeconds: number;
    alertRadius?: number;
    curl?: { seconds: number; damageFactor: number };
    burrowAfter?: number;
    cornered?: { distance: number; attack: string };
  };
  attacks: readonly CreatureAttack[];
  fauna?: FaunaRules;
  haunt?: HauntRules;
  boss?: BossRules;
}

/**
 * Tamanho de todas as criaturas sobre os corpos da tabela: o boneco (raiz no cliente) e o corpo do
 * golpe, das flechas e da colisão crescem juntos, e os ataques presos ao corpo ganham o que o raio
 * cresceu (`grownAttack`): o alcance continua o mesmo a partir da pele.
 */
export const CREATURE_SCALE = 1.25;

/** Escala da assombração reforçada (doc 01, Ondas), sobre `CREATURE_SCALE`; o chefe reforçado não cresce. */
export const ELITE_SCALE = 1.3;

/** Fauna neutra e assombrações (doc 06, Bestiário), nos corpos da tabela (sem `CREATURE_SCALE`). */
const CREATURE_TABLE: readonly CreatureType[] = [
  {
    id: "capivara",
    index: 0,
    name: "Cervo",
    archetype: "neutra",
    hp: 60,
    speed: 2.5,
    fleeSpeed: 7,
    perception: 10,
    crouchPerception: 5,
    radius: 0.45,
    height: 0.75,
    defense: 0,
    weaknesses: [],
    poise: 0.3,
    fauna: { flock: [3, 5], respawn: 90, zone: { biomes: { mangue: 1, praia: 0.7, mata: 0.5 }, maxCoastDistance: 60, maxSlope: 0.3, density: 0.85, radius: 16 } },
    drops: [
      { item: "carne-crua", min: 2, max: 3, chance: 1 },
      { item: "couro", min: 1, max: 2, chance: 0.6 },
    ],
    behavior: { fleeSeconds: 12, alertRadius: 15 },
    attacks: [],
  },
  {
    id: "tatu",
    index: 1,
    name: "Ouriço",
    archetype: "neutra",
    hp: 40,
    speed: 2,
    fleeSpeed: 5,
    perception: 6,
    crouchPerception: 3,
    radius: 0.35,
    height: 0.45,
    defense: 0,
    weaknesses: [],
    poise: 0.4,
    fauna: { flock: [2, 4], respawn: 90, zone: { biomes: { cerrado: 1, mata: 0.3 }, maxSlope: 0.45, density: 0.45, radius: 18 } },
    drops: [
      { item: "carne-crua", min: 1, max: 1, chance: 1 },
      { item: "couro", min: 1, max: 2, chance: 0.8 },
    ],
    behavior: { fleeSeconds: 20, curl: { seconds: 3, damageFactor: 0.3 }, burrowAfter: 20 },
    attacks: [],
  },
  {
    id: "ema",
    index: 2,
    name: "Javali",
    archetype: "neutra",
    hp: 50,
    speed: 4,
    fleeSpeed: 10,
    perception: 14,
    crouchPerception: 7,
    radius: 0.4,
    height: 1.6,
    defense: 0,
    weaknesses: [],
    poise: 0.35,
    fauna: { flock: [3, 4], respawn: 90, zone: { biomes: { cerrado: 1, praia: 0.2 }, maxSlope: 0.2, density: 0.6, radius: 22 } },
    drops: [
      { item: "carne-crua", min: 2, max: 2, chance: 1 },
      { item: "couro", min: 1, max: 1, chance: 0.5 },
    ],
    behavior: { fleeSeconds: 10, alertRadius: 20, cornered: { distance: 2, attack: "chute" } },
    attacks: [{ id: "chute", kind: "golpe", windup: 0.5, recovery: 0.6, shape: "arco", range: 2, angle: 60, damage: 10, types: ["contundente"], use: [0, 2] }],
  },
  // Assombrações (doc 06, Bestiário; custo, dia de entrada, pico e peso do doc 01, Ondas). Caçam
  // sempre (percepção da ilha toda) e não fogem.
  {
    id: "corpo-seco",
    index: 3,
    name: "Esqueleto",
    archetype: "assombracao",
    hp: 120,
    speed: 3.2,
    fleeSpeed: 3.2,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.4,
    height: 1.8,
    defense: 0,
    weaknesses: [],
    poise: 0.35,
    drops: [{ item: "tostao", min: 4, max: 9, chance: 1 }, { item: "madeira", min: 1, max: 3, chance: 0.3 }],
    behavior: { fleeSeconds: 0 },
    attacks: [{ id: "garra", kind: "golpe", windup: 0.7, recovery: 1, shape: "arco", range: 2.2, angle: 100, damage: 18, types: ["contundente"], use: [0, 2.2] }],
    haunt: { cost: 1, startDay: 0, peakDay: 0, maxWeight: 1, coins: [4, 9], target: "custo" },
  },
  {
    id: "lobisomem",
    index: 4,
    name: "Lobisomem",
    archetype: "assombracao",
    hp: 70,
    speed: 6.5,
    fleeSpeed: 6.5,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.45,
    height: 1.9,
    defense: 0,
    weaknesses: [],
    poise: 0.3,
    drops: [{ item: "tostao", min: 5, max: 12, chance: 1 }, { item: "couro", min: 1, max: 3, chance: 0.5 }],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "mordida", kind: "golpe", windup: 0.5, recovery: 0.6, shape: "arco", range: 2, angle: 90, damage: 14, types: ["cortante"], use: [0, 2] },
      { id: "salto", kind: "salto", windup: 0.6, recovery: 0.3, shape: "linha", range: 9, angle: 0, width: 1.4, damage: 24, types: ["cortante"], use: [4, 9], speed: 14, vulnerable: 0.8, every: [3, 5] },
    ],
    haunt: { cost: 2, startDay: 1, peakDay: 5, maxWeight: 0.8, coins: [5, 12], target: "isolado", orbit: [6, 10] },
  },
  {
    id: "mula-sem-cabeca",
    index: 5,
    name: "Pesadelo",
    archetype: "assombracao",
    hp: 110,
    speed: 5,
    fleeSpeed: 5,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.55,
    height: 1.6,
    defense: 0,
    weaknesses: [],
    poise: 0.4,
    drops: [{ item: "tostao", min: 8, max: 14, chance: 1 }],
    behavior: { fleeSeconds: 0 },
    attacks: [
      {
        id: "carga",
        kind: "carga",
        windup: 1,
        recovery: 0.6,
        shape: "linha",
        range: 25,
        angle: 0,
        width: 2,
        damage: 22,
        types: ["contundente", "fogo"],
        use: [8, 25],
        speed: 13,
        vulnerable: 1.5,
        every: [3, 5],
        hazard: { kind: "fogo", radius: 0.75, seconds: 4, tick: 0.5, damage: 6 },
      },
    ],
    haunt: { cost: 3, startDay: 3, peakDay: 8, maxWeight: 0.6, coins: [8, 14], target: "custo" },
  },
  {
    id: "matinta-pereira",
    index: 6,
    name: "Harpia",
    archetype: "assombracao",
    hp: 60,
    speed: 7,
    fleeSpeed: 7,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.4,
    height: 0.8,
    defense: 0,
    weaknesses: [],
    poise: 0.35,
    drops: [{ item: "tostao", min: 5, max: 10, chance: 1 }],
    behavior: { fleeSeconds: 0 },
    attacks: [{ id: "mergulho", kind: "mergulho", windup: 0.8, recovery: 0.3, shape: "circulo", range: 2.5, angle: 360, damage: 16, types: ["perfurante"], use: [0, 18], speed: 18, vulnerable: 1.2, every: [4, 6] }],
    haunt: { cost: 3, startDay: 4, peakDay: 9, maxWeight: 0.6, coins: [5, 10], target: "aberto", orbit: [10, 15], fly: { altitude: 6 } },
  },
  {
    id: "cuca",
    index: 7,
    name: "Bruxa",
    archetype: "assombracao",
    hp: 80,
    speed: 3.5,
    fleeSpeed: 3.5,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.45,
    height: 1.7,
    defense: 0,
    weaknesses: [],
    poise: 0.35,
    drops: [{ item: "tostao", min: 6, max: 12, chance: 1 }, { item: "mandioca", min: 1, max: 1, chance: 0.4 }],
    behavior: { fleeSeconds: 0 },
    attacks: [
      {
        id: "caldeirao",
        kind: "arremesso",
        windup: 1,
        recovery: 0.5,
        shape: "circulo",
        range: 3,
        angle: 360,
        damage: 20,
        edge: 0.5,
        types: ["agua"],
        use: [6, 22],
        flight: [0.9, 1.4],
        every: [3, 5],
        hazard: { kind: "poca", radius: 3, seconds: 4, tick: 0.5, damage: 5, slow: 0.4 },
      },
      { id: "colherada", kind: "golpe", windup: 0.5, recovery: 0.6, shape: "arco", range: 2, angle: 90, damage: 12, types: ["contundente"], use: [0, 2] },
    ],
    haunt: { cost: 4, startDay: 5, peakDay: 10, maxWeight: 0.5, coins: [6, 12], target: "custo", keepDistance: [12, 20] },
  },
  {
    id: "capelobo",
    index: 8,
    name: "Troll",
    archetype: "assombracao",
    hp: 220,
    speed: 2.8,
    fleeSpeed: 2.8,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.7,
    height: 2.4,
    defense: 0,
    weaknesses: [],
    poise: 1,
    drops: [
      { item: "tostao", min: 15, max: 25, chance: 1 },
      { item: "couro", min: 3, max: 5, chance: 1 },
      { item: "minerio-ferro", min: 1, max: 3, chance: 0.3 },
    ],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "pancada", kind: "golpe", windup: 1.2, recovery: 1, shape: "circulo", range: 5, angle: 360, damage: 35, edge: 0.5, types: ["contundente"], use: [0, 4] },
      { id: "agarrao", kind: "agarrar", windup: 0.9, recovery: 0.8, shape: "arco", range: 2.5, angle: 90, damage: 20, types: ["contundente"], use: [0, 2.5], every: [10, 14], push: 6 },
    ],
    haunt: { cost: 6, startDay: 7, peakDay: 12, maxWeight: 0.35, coins: [15, 25], target: "dano", staggerImmune: true },
  },
  // Chefes (doc 06, Chefes): vida e dano do dia 0, antes da escala do dia, da dificuldade e dos jogadores.
  {
    id: "mapinguari",
    index: 9,
    name: "Ciclope",
    article: "o",
    archetype: "chefe",
    hp: 1800,
    speed: 4.5,
    fleeSpeed: 4.5,
    perception: 2048,
    crouchPerception: 2048,
    radius: 1.1,
    height: 3.6,
    defense: 0,
    weaknesses: [],
    poise: 1,
    drops: [{ item: "tostao", min: 120, max: 200, chance: 1 }],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "pancada", kind: "golpe", windup: 1.4, recovery: 1, shape: "arco", range: 6, angle: 120, damage: 50, types: ["contundente"], use: [0, 5.5], pulses: { count: 2, every: 0.45 }, phase: 1 },
      { id: "pancada-investida", kind: "golpe", windup: 1.4, recovery: 0.3, shape: "arco", range: 6, angle: 120, damage: 50, types: ["contundente"], use: [0, 5.5], pulses: { count: 2, every: 0.45 }, phase: 2, then: "investida", group: "pancada" },
      { id: "investida", kind: "carga", windup: 1, recovery: 1, shape: "linha", range: 16, angle: 0, width: 3, damage: 40, types: ["contundente"], use: [99, 99], speed: 10, vulnerable: 1.2, phase: 2 },
      { id: "tronco", kind: "arremesso", windup: 0.8, recovery: 0.8, shape: "circulo", range: 3.5, angle: 360, damage: 40, edge: 0.5, types: ["contundente"], use: [8, 32], flight: [1.2, 1.2], every: [6, 9] },
      { id: "bafo", kind: "golpe", windup: 1, recovery: 1, shape: "arco", range: 8, angle: 60, damage: 15, types: ["contundente"], use: [0, 7.5], pulses: { count: 4, every: 0.5 }, patuaBlock: 6, every: [10, 14] },
      { id: "rochedo", kind: "marcas", windup: 2, recovery: 0.8, shape: "circulo", range: 4, angle: 360, damage: 90, types: ["contundente"], use: [0, 30], marks: { count: 1, gap: 0 }, share: true, clip: "tronco", every: [16, 22] },
    ],
    boss: { phase2: 0.5 },
  },
  {
    id: "cabra-cabriola",
    index: 10,
    name: "Quimera",
    article: "a",
    archetype: "chefe",
    hp: 1400,
    speed: 7,
    fleeSpeed: 7,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.8,
    height: 2.2,
    defense: 0,
    weaknesses: [],
    poise: 1,
    drops: [{ item: "tostao", min: 100, max: 180, chance: 1 }],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "investidas", kind: "carga", windup: 0.8, recovery: 0.3, shape: "linha", range: 18, angle: 0, width: 2.5, damage: 30, types: ["contundente"], use: [5, 20], speed: 15, vulnerable: 0.8, every: [7, 10], chain: 3, phase: 1 },
      { id: "marrada", kind: "marcas", windup: 1.6, recovery: 0.8, shape: "circulo", range: 2, angle: 360, damage: 70, types: ["contundente"], use: [0, 20], marks: { count: 1, gap: 0 }, buster: true, clip: "investidas", every: [12, 16] },
      {
        id: "investidas-fogo",
        kind: "carga",
        windup: 0.8,
        recovery: 0.3,
        shape: "linha",
        range: 18,
        angle: 0,
        width: 2.5,
        damage: 30,
        types: ["contundente"],
        use: [5, 20],
        speed: 15,
        vulnerable: 0.8,
        every: [7, 10],
        chain: 3,
        phase: 2,
        group: "investidas",
        hazard: { kind: "fogo", radius: 1.25, seconds: 5, tick: 0.5, damage: 8 },
      },
      { id: "fogo", kind: "golpe", windup: 1, recovery: 0.8, shape: "arco", range: 10, angle: 45, damage: 12, types: ["fogo"], use: [0, 9.5], pulses: { count: 6, every: 0.25 }, every: [6, 9] },
      { id: "coice", kind: "golpe", windup: 0.5, recovery: 0.6, shape: "arco", range: 3, angle: 120, damage: 30, types: ["contundente"], use: [0, 3], rear: true, push: 8, every: [3, 5] },
    ],
    boss: { phase2: 0.5 },
  },
  {
    id: "cuca-rainha",
    index: 11,
    name: "Rainha-Bruxa",
    article: "a",
    archetype: "chefe",
    hp: 1500,
    speed: 4,
    fleeSpeed: 4,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.6,
    height: 2.4,
    defense: 0,
    weaknesses: [],
    poise: 1,
    drops: [
      { item: "tostao", min: 100, max: 180, chance: 1 },
      { item: "resina-jatoba", min: 2, max: 3, chance: 1 },
    ],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "caldeiroes", kind: "marcas", windup: 1, recovery: 0.8, shape: "circulo", range: 3, angle: 360, damage: 30, edge: 0.5, types: ["agua"], use: [0, 30], marks: { count: 5, gap: 0.4 }, every: [6, 9], phase: 1 },
      {
        id: "caldeiroes-pocas",
        kind: "marcas",
        windup: 1,
        recovery: 0.8,
        shape: "circulo",
        range: 3,
        angle: 360,
        damage: 30,
        edge: 0.5,
        types: ["agua"],
        use: [0, 30],
        marks: { count: 5, gap: 0.4 },
        every: [6, 9],
        phase: 2,
        group: "caldeiroes",
        hazard: { kind: "poca", radius: 3, seconds: 45, tick: 0.5, damage: 5, slow: 0.4 },
      },
      { id: "cantiga", kind: "golpe", windup: 2.2, recovery: 0.8, shape: "arco", range: 12, angle: 50, damage: 15, types: ["agua"], use: [0, 11.5], slow: { factor: 0.4, seconds: 2 }, interruptible: true, every: [8, 12] },
    ],
    boss: {
      phase2: 0.4,
      summon: { type: "corpo-seco", count: 2, perTwoPlayers: 1, every: 25, max: 6 },
      vanish: { every: 12, seconds: 0.6, distance: [15, 25] },
      keepDistance: [8, 16],
    },
  },
  {
    id: "corpo-seco-anciao",
    index: 12,
    name: "Rei Esqueleto",
    article: "o",
    archetype: "chefe",
    hp: 2400,
    speed: 2.5,
    fleeSpeed: 2.5,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.9,
    height: 3,
    defense: 0,
    weaknesses: ["fogo"],
    poise: 1,
    drops: [{ item: "tostao", min: 120, max: 200, chance: 1 }],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "raizes", kind: "marcas", windup: 1, recovery: 1, shape: "circulo", range: 2.5, angle: 360, damage: 45, types: ["perfurante"], use: [0, 25], marks: { count: 4, gap: 0, perPlayer: [1, 4] }, root: 1, every: [8, 11] },
      { id: "varrida", kind: "golpe", windup: 1.3, recovery: 1.2, shape: "arco", range: 5, angle: 270, damage: 50, types: ["contundente"], use: [0, 4.5] },
      { id: "lamento", kind: "golpe", windup: 2.6, recovery: 1, shape: "circulo", range: 14, angle: 360, damage: 40, edge: 0.6, types: ["perfurante"], use: [0, 12], interruptible: true, clip: "raizes", every: [18, 24] },
    ],
    boss: {
      phase2: 0.5,
      legendaryChance: 0.3,
      summon: { type: "corpo-seco", count: 2, perTwoPlayers: 0, every: 20, max: 6, phase: 2 },
      aura: { radius: 6, hungerMul: 3 },
    },
  },
  // Guardiões dos Muiraquitãs (doc 06): kit-base comum e uma mecânica por cor.
  guardian(13, "verde", "Guardião da Esmeralda", [areaAttack({ kind: "mato", radius: 4, seconds: 3, tick: 0.5, damage: 0, slow: 0.5 })]),
  guardian(14, "azul", "Guardião da Safira", [
    areaAttack(),
    { id: "onda", kind: "golpe", windup: 0.8, recovery: 0.8, shape: "linha", range: 12, angle: 0, width: 3, damage: 25, types: ["agua"], use: [3, 11], push: 8, every: [6, 9] },
  ]),
  guardian(15, "vermelho", "Guardião do Rubi", [areaAttack({ kind: "brasa", radius: 3, seconds: 4, tick: 0.5, damage: 6 })]),
  guardian(16, "amarelo", "Guardião do Topázio", [
    areaAttack(),
    { id: "raios", kind: "golpe", windup: 0.6, recovery: 0.25, shape: "linha", range: 30, angle: 0, width: 1.2, damage: 20, types: ["raio"], use: [6, 28], lead: 0.5, chain: 3, every: [7, 10] },
  ]),
  guardian(17, "rosa", "Guardião do Quartzo", [areaAttack()], { at: [2 / 3, 1 / 3], count: 2 }),
  // Boitatá (doc 01, Encontro final): serpente de fogo, Mergulho (só projéteis acertam) e Abraço na proa.
  {
    id: "boitata",
    index: 18,
    name: "Dragão",
    article: "o",
    archetype: "final",
    hp: 4000,
    speed: 14,
    fleeSpeed: 14,
    perception: 2048,
    crouchPerception: 2048,
    radius: 1.4,
    height: 3,
    defense: 0,
    weaknesses: [],
    poise: 1,
    drops: [],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "cuspe", kind: "marcas", windup: 1, recovery: 0.2, shape: "circulo", range: 3, angle: 360, damage: 40, edge: 0.6, types: ["fogo"], use: [0, 200], marks: { count: 1, gap: 0 }, lead: 1, leadCap: 1 },
      { id: "bote", kind: "golpe", windup: 0.8, recovery: 0.5, shape: "arco", range: 7, angle: 60, damage: 70, types: ["perfurante"], use: [0, 30] },
      { id: "chicote", kind: "golpe", windup: 0.9, recovery: 0.6, shape: "circulo", range: 10, inner: 4, angle: 360, damage: 35, types: ["contundente"], use: [0, 30], groundOnly: true, toss: 9 },
      { id: "sopro", kind: "golpe", windup: 1.2, recovery: 0.5, shape: "arco", range: 14, angle: 40, damage: 10, types: ["fogo"], use: [0, 30], pulses: { count: 8, every: 0.25 }, minPhase: 2 },
      { id: "rastro", kind: "marcas", windup: 0.8, recovery: 0.3, shape: "circulo", range: 2.5, angle: 360, damage: 0, types: ["fogo"], use: [0, 30], marks: { count: 2, gap: 0.4 }, minPhase: 2, hazard: { kind: "brasa", radius: 2.5, seconds: 6, tick: 0.5, damage: 6 } },
      { id: "chuva", kind: "marcas", windup: 1, recovery: 0.5, shape: "circulo", range: 3, angle: 360, damage: 30, types: ["fogo"], use: [0, 30], marks: { count: 8, gap: 0.4 }, minPhase: 3 },
    ],
    boss: { phase2: 0.66, phases: [0.66, 0.33], hitCap: 0.025 },
  },
  // Inimigos maiores (F89): versões grandes de assombrações de antes, com os mesmos ataques mais fortes.
  // Entram nas levas só do dia 10 em diante (as noites de 0 a 9 não mudam; o desafio da Pedra de Encanto,
  // que traz as do dia + 2, os traz dois dias antes) e não são chefes. O raio do corpo fica
  // em até 0,8 m (o dos chefes), para nascer e andar entre as árvores.
  {
    id: "lobisomem-alfa",
    index: 19,
    name: "Lobisomem Alfa",
    archetype: "assombracao",
    hp: 170,
    speed: 7,
    fleeSpeed: 7,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.6,
    height: 2.5,
    defense: 0,
    weaknesses: [],
    poise: 0.45,
    drops: [{ item: "tostao", min: 10, max: 18, chance: 1 }, { item: "couro", min: 2, max: 4, chance: 0.8 }],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "mordida", kind: "golpe", windup: 0.5, recovery: 0.6, shape: "arco", range: 2.6, angle: 90, damage: 20, types: ["cortante"], use: [0, 2.6] },
      { id: "salto", kind: "salto", windup: 0.6, recovery: 0.3, shape: "linha", range: 11, angle: 0, width: 1.8, damage: 32, types: ["cortante"], use: [4, 11], speed: 15, vulnerable: 0.8, every: [3, 5] },
    ],
    haunt: { cost: 4, startDay: 10, peakDay: 14, maxWeight: 0.4, coins: [10, 18], target: "isolado", orbit: [7, 12] },
  },
  {
    id: "corpo-seco-colosso",
    index: 20,
    name: "Esqueleto Colosso",
    archetype: "assombracao",
    hp: 360,
    speed: 2.6,
    fleeSpeed: 2.6,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.7,
    height: 3.2,
    defense: 0,
    weaknesses: [],
    poise: 0.8,
    drops: [{ item: "tostao", min: 14, max: 24, chance: 1 }, { item: "minerio-ferro", min: 1, max: 2, chance: 0.4 }],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "garra", kind: "golpe", windup: 0.8, recovery: 1, shape: "arco", range: 3, angle: 110, damage: 26, types: ["contundente"], use: [0, 3] },
      { id: "pancada", kind: "golpe", windup: 1.3, recovery: 1, shape: "circulo", range: 5.5, angle: 360, damage: 40, edge: 0.5, types: ["contundente"], use: [0, 4.5], every: [6, 9] },
    ],
    haunt: { cost: 5, startDay: 11, peakDay: 15, maxWeight: 0.35, coins: [14, 24], target: "custo" },
  },
  {
    id: "capelobo-ancestral",
    index: 21,
    name: "Troll de Pedra",
    archetype: "assombracao",
    hp: 400,
    speed: 2.5,
    fleeSpeed: 2.5,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.8,
    height: 3.2,
    defense: 0,
    weaknesses: [],
    poise: 1,
    drops: [
      { item: "tostao", min: 22, max: 36, chance: 1 },
      { item: "couro", min: 3, max: 5, chance: 1 },
      { item: "minerio-ferro", min: 2, max: 4, chance: 0.5 },
    ],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "pancada", kind: "golpe", windup: 1.3, recovery: 1, shape: "circulo", range: 6, angle: 360, damage: 45, edge: 0.5, types: ["contundente"], use: [0, 5] },
      { id: "agarrao", kind: "agarrar", windup: 0.9, recovery: 0.8, shape: "arco", range: 3, angle: 90, damage: 26, types: ["contundente"], use: [0, 3], every: [10, 14], push: 7 },
    ],
    haunt: { cost: 7, startDay: 13, peakDay: 18, maxWeight: 0.3, coins: [22, 36], target: "dano", staggerImmune: true },
  },
];

/** Ataques que saem do corpo da criatura (o arco, a faixa e o círculo em volta dela); `marcas` e `arremesso` caem num ponto do chão. */
const BODY_ATTACKS: ReadonlySet<AttackKind> = new Set(["golpe", "agarrar", "carga", "salto", "mergulho"]);

/**
 * O ataque com o corpo `grow` m mais largo: os de corpo alcançam o mesmo além da pele (alcance e anel
 * vazado + `grow`); a distância em que se escolhe cada ataque, medida do centro, também anda `grow`
 * (o mínimo 0 continua 0). Área num ponto do chão (marcas, arremesso) fica do mesmo tamanho.
 */
export function grownAttack(attack: CreatureAttack, grow: number): CreatureAttack {
  const use: readonly [number, number] = [attack.use[0] > 0 ? attack.use[0] + grow : 0, attack.use[1] + grow];
  if (!BODY_ATTACKS.has(attack.kind)) return { ...attack, use };
  return { ...attack, use, range: attack.range + grow, ...(attack.inner !== undefined && { inner: attack.inner + grow }) };
}

/** Fauna neutra e assombrações (doc 06, Bestiário). Os índices vão no schema e nas zonas de fauna. */
const GROWTH: ReadonlyMap<string, number> = new Map(CREATURE_TABLE.map((t) => [t.id, t.radius * (CREATURE_SCALE - 1)]));

export const CREATURE_TYPES: readonly CreatureType[] = CREATURE_TABLE.map((t) => {
  const grow = GROWTH.get(t.id)!;
  return { ...t, radius: t.radius * CREATURE_SCALE, height: t.height * CREATURE_SCALE, attacks: t.attacks.map((a) => grownAttack(a, grow)) };
});

/** Área prevista do kit-base (doc 06): no ponto futuro do alvo (70 %), 1,0 s, raio de 4 m. */
function areaAttack(hazard?: AttackHazard): CreatureAttack {
  return { id: "area", kind: "marcas", windup: 1, recovery: 0.8, shape: "circulo", range: 4, angle: 360, damage: 45, edge: 0.6, types: ["contundente"], use: [4, 25], marks: { count: 1, gap: 0 }, lead: 0.7, every: [5, 8], ...(hazard ? { hazard } : {}) };
}

/**
 * Guardião (doc 06): sentinela encantada de 1100 de vida, combo de 2 golpes (0,5 s e 0,9 s, arco de
 * 120° × 3 m), área prevista e salto até o alvo (anel de 5 m); coleira de 60 m e 2 %/s de
 * regeneração com o grupo longe; sem quebras de postura.
 */
function guardian(index: number, color: string, name: string, extra: readonly CreatureAttack[], decoys?: BossRules["decoys"]): CreatureType {
  return {
    id: `guardiao-${color}`,
    index,
    name,
    article: "o",
    archetype: "guardiao",
    hp: 1100,
    speed: 5.5,
    fleeSpeed: 5.5,
    perception: 2048,
    crouchPerception: 2048,
    radius: 0.7,
    height: 2.6,
    defense: 0,
    weaknesses: [],
    poise: 1,
    drops: [{ item: "tostao", min: 60, max: 100, chance: 1 }],
    behavior: { fleeSeconds: 0 },
    attacks: [
      { id: "golpe-1", kind: "golpe", windup: 0.5, recovery: 0.15, shape: "arco", range: 3, angle: 120, damage: 30, types: ["contundente"], use: [0, 2.4], then: "golpe-2" },
      { id: "golpe-2", kind: "golpe", windup: 0.9, recovery: 0.8, shape: "arco", range: 3, angle: 120, damage: 45, types: ["contundente"], use: [99, 99] },
      { id: "salto", kind: "marcas", windup: 1, recovery: 1, shape: "circulo", range: 5, angle: 360, damage: 50, edge: 0.5, types: ["contundente"], use: [8, 30], marks: { count: 1, gap: 0 }, leap: true, every: [8, 12] },
      ...extra,
    ],
    boss: { phase2: 0, noBreaks: true, leash: 60, regen: 0.02, ...(decoys ? { decoys } : {}) },
  };
}

export const CREATURE_BY_ID: ReadonlyMap<string, CreatureType> = new Map(CREATURE_TYPES.map((t) => [t.id, t]));

/** Quanto o raio da criatura cresceu com `CREATURE_SCALE` (m): o que os ataques de corpo ganham de alcance. */
export function bodyGrowth(id: string): number {
  return GROWTH.get(id) ?? 0;
}

export function creatureType(id: string): CreatureType | undefined {
  return CREATURE_BY_ID.get(id);
}

/** Durações dos estados comuns (doc 06): surgir do chão, cambalear, cavar, ficar caída. */
export const CREATURE_TIMING = {
  emerge: 1.2,
  stagger: 0.6,
  /** Janela da postura. */
  poiseWindow: 2,
  burrow: 1.2,
  corpse: 1.5,
  /** Vaga para um novo ponto da zona a cada ~12 s. */
  wanderEvery: 12,
  /** Simula a zona só com algum jogador a menos disto (m), e a esvazia depois de vazia por `idleDespawn` s. */
  activeRadius: 120,
  idleDespawn: 60,
  /** Travamento: andou menos que isto (m) em `stuckWindow` s tentando andar. */
  stuckDistance: 0.3,
  stuckWindow: 2,
  /** Drops ficam reservados a quem deu o golpe final por 3 s. */
  dropLock: 3,
} as const;
